import { fetchGetCreditCardAccount } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { Card } from './types';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { daysAgo } from '../helpers';

type TCreditCardDashboardData = {
    accountCreatedAt: Date;
    accountId: string;
    totalOutstandingBalanceRaw: number;
    repaymentAmount: number;
    availableCredit: number;
    totalOutstandingBalance: number;
    dueDate: Date | null;
    cards: Card[];
    activeCard?: Card;
    cardsToDisplay: Card[];
};

const statusPriority: Record<Card['status'], number> = {
    active: 1,
    frozen: 2,
    blocked: 3,
    lost_or_stolen: 4,
    closed: 5,
    expired: 6
};

const handleNegativeNumber = (num: number) => (num === 0 ? 0 : -num);

export const useGetCreditCardDashboardData = (): TCreditCardDashboardData => {
    const stateKey = useStateKey();

    const { data: credit_card_account } = useQuery({
        queryKey: ['credit_card_account', stateKey],
        queryFn: () => fetchGetCreditCardAccount({ stateKey }),
        select: (data) => data.data.account,
        suspense: true
    });

    if (!credit_card_account || !credit_card_account.id) {
        throw new Error('Credit card account not found');
    }

    const activeCards = credit_card_account.cards?.filter(
        (card) => card.status === 'active' || card.status === 'frozen'
    );
    const activeCard = activeCards?.[0];

    const cardsToDisplay = (credit_card_account.cards || []).filter((card) => {
        if (card.status === 'active' || card.status === 'frozen') {
            return true;
        }
        return new Date(card.status_date) > daysAgo(10);
    });
    const sortedCards = cardsToDisplay.sort(
        (a, b) =>
            statusPriority[a.status] - statusPriority[b.status] ||
            new Date(b.status_date).getTime() -
                new Date(a.status_date).getTime()
    );

    return {
        accountId: credit_card_account.id,
        accountCreatedAt: new Date(credit_card_account.created_at!),
        totalOutstandingBalanceRaw:
            credit_card_account.total_outstanding_balance!,
        repaymentAmount: credit_card_account.repayment_amount!,
        totalOutstandingBalance: handleNegativeNumber(
            credit_card_account.total_outstanding_balance!
        ),
        availableCredit: credit_card_account.available_credit!,
        dueDate: credit_card_account.due_date
            ? new Date(credit_card_account.due_date)
            : null,
        cards: credit_card_account.cards || [],
        activeCard: activeCard,
        cardsToDisplay: sortedCards
    };
};
